/**
 * @fileoverview Frontmatter token interpolation.
 *
 * Legal documents reference frontmatter values inline via
 * `{tokenName}` placeholders so per-app variations (product name,
 * contact email, vendor) live in one place. The interpolator resolves
 * tokens at render time. Unknown tokens THROW — never silently leave
 * a `{...}` artifact in published legal copy.
 *
 * Token grammar: `\{([a-zA-Z][a-zA-Z0-9]*)\}` — letter-led identifier,
 * letters + digits, no whitespace, no nesting. Liberal enough to cover
 * every frontmatter key; strict enough to ignore JSON braces (`{`).
 *
 * @module @puffin-core/legal-content/registry/interpolate
 */

const TOKEN_RE = /\{([a-zA-Z][a-zA-Z0-9]*)\}/g;

/**
 * Replace every `{token}` in `template` with the corresponding value
 * from `frontmatter`. Throws if any token is missing.
 *
 * @param {string} template — Source string.
 * @param {Object<string, string>} frontmatter — Token → value lookup.
 * @returns {string} Resolved string.
 * @throws {Error} If `template` references a key not present in `frontmatter`.
 */
export function interpolate(template, frontmatter) {
  return template.replace(TOKEN_RE, (match, key) => {
    if (!Object.prototype.hasOwnProperty.call(frontmatter, key)) {
      throw new Error(
        `interpolate: unknown frontmatter token "${match}" (template: "${template}")`,
      );
    }
    return String(frontmatter[key]);
  });
}

/**
 * Return the unique token names referenced in `template`, in order
 * of first appearance.
 *
 * @param {string} template
 * @returns {string[]}
 */
export function extractTokens(template) {
  const seen = new Set();
  const ordered = [];
  let match;
  TOKEN_RE.lastIndex = 0;
  while ((match = TOKEN_RE.exec(template)) !== null) {
    const key = match[1];
    if (!seen.has(key)) {
      seen.add(key);
      ordered.push(key);
    }
  }
  return ordered;
}
